<template>
    <div>
        <div class="row items-center no-wrap">
            <q-toggle v-if="!isFromMultiLicenseSelection || (isFromMultiLicenseSelection && !expirySyncDateIsEnabled)"
                      v-model="expirySyncDateIsEnabled"
                      :disabled="getAllAccountSettingsPending"
                      @input="confirmToggleExpirySyncDateSetting" />
            <div v-if="isFromMultiLicenseSelection && expirySyncDateIsEnabled"
                 class="self-start">
                <transition name="fade">
                    <q-icon name="mdi-information"
                            :disabled="getAllAccountSettingsPending"
                            class="cursor-pointer q-pr-sm"
                            style="color: rgba(0, 0, 0, 0.24);">
                        <q-popup-proxy content-class="bg-transparent no-shadow q-pb-sm q-pr-sm"
                                       anchor="top left"
                                       self="bottom left"
                                       transition-show="scale"
                                       transition-hide="scale">
                            <q-banner class="bg-secondary text-white box shadow-2"
                                      style="max-width: 265px;">
                                <span class="text-caption">
                                    This setting can be changed or disabled from your <router-link to='/myaccount/licenses' style="text-decoration-line: underline;" class="text-white cursor-pointer">Licenses & Activations</router-link> page.
                                </span>
                            </q-banner>
                        </q-popup-proxy>
                    </q-icon>
                </transition>
            </div>
            <div :class="!isFromMultiLicenseSelection ? 'row' : ''"
                 :style="isFromMultiLicenseSelection ? expirySyncDateIsEnabled ? '' : 'max-width: 175px;' : ''">
                {{ expirySyncDateToggleLabel }}
                <div v-if="expirySyncDateIsEnabled" class="text-weight-medium text-primary" style="text-wrap-mode: nowrap;">
                    <div v-if="!isFromMultiLicenseSelection"
                          @click="editExpirySyncDateSetting"
                          style="text-decoration: underline;"
                          class="text-primary cursor-pointer q-pl-xs">
                        {{ expirySyncDateToggleLabelDate }}
                        <q-icon name="mdi-pencil"
                                color="primary"
                                style="text-decoration: underline; vertical-align: baseline;" />
                    </div>
                    <div v-else
                          style="font-size: 1.25rem;">
                        {{ expirySyncDateToggleLabelDate }}
                    </div>
                </div>
            </div>
            <div v-if="!(isFromMultiLicenseSelection && expirySyncDateIsEnabled)" >
                <transition name="fade">
                    <q-icon name="mdi-information"
                            :disabled="getAllAccountSettingsPending"
                            class="cursor-pointer q-pl-sm"
                            style="color: rgba(0, 0, 0, 0.24);">
                        <q-popup-proxy content-class="bg-transparent no-shadow q-pb-sm q-pl-sm"
                                       anchor="top left"
                                       self="bottom left"
                                       transition-show="scale"
                                       transition-hide="scale">
                            <q-banner class="bg-secondary text-white box shadow-2"
                                      style="max-width: 425px;">
                                <span class="text-caption">{{ expirySyncDateDescription }}</span>
                            </q-banner>
                        </q-popup-proxy>
                    </q-icon>
                </transition>
            </div>
        </div>
        <q-dialog v-model="showConfirmationDialog" @hide="cancelExpirySyncDateSettingChanges">
            <q-card class="col q-pa-sm"
                    :style="!screenSize.xs ? 'max-width: 450px; min-width: 275px;' : 'min-width: 275px;'">
                <q-card-section>
                    <div class="text-h6 row items-center justify-center q-mb-sm">
                        <div class="col-xs-2 col-sm-1">
                            <q-icon name="mdi-calendar" size="sm" class="q-mr-xs" />
                        </div>
                        <div class="col-xs-10 col-sm-11">
                            Turn On The Preferred License Expiry Date Setting?
                        </div>
                    </div>
                    <div class="q-mt-md row q-pa-sm">
                            {{ expirySyncDateDescription }}
                    </div>
                    <div class="row q-col-gutter-sm q-mt-md">
                        <div class="col-xs-12 col-sm-auto">
                            <q-btn label="Continue" color="primary" class="full-width" @click="toggleExpirySyncDateSetting" />
                        </div>
                        <div class="col-xs-12 col-sm-auto">
                            <q-btn flat label="Cancel" color="primary" v-close-popup class="full-width" @click="cancelExpirySyncDateSettingChanges" />
                        </div>
                    </div>
                </q-card-section>
            </q-card>
        </q-dialog>
        <q-dialog v-model="showExpirySyncDateEditor" @hide="cancelExpirySyncDateSettingChanges">
            <q-card class="col q-pa-sm"
                    :style="!screenSize.xs ? 'max-width: 450px' : ''">
                <q-card-section>
                    <expiry-sync-date-editor :expirySyncDateSetting="expirySyncDateSetting"
                                             @expirySyncDateSettingUpdated="expirySyncDateSettingUpdated"
                                             @cancel="cancelExpirySyncDateSettingChanges" />
                </q-card-section>
            </q-card>
        </q-dialog>
    </div>
</template>
<script> 
    import { mapState } from 'vuex'
    import { cloneDeep } from 'lodash'
    import { format } from 'date-fns'
    import expirySyncDateEditor from '@/components/ExpirySyncDateEditor'

    export default {
        name: 'expiry-sync-date-setting',
        components: {
            expirySyncDateEditor,
        },
        props: {
            isFromMultiLicenseSelection: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                expirySyncDateSetting: null,
                expirySyncDateIsEnabled: false,
                expirySyncDateDescription: '',
                expirySyncDateToggleLabel: '',
                expirySyncDateToggleLabelDate: '',
                showConfirmationDialog: false,
                showExpirySyncDateEditor: false,
            }
        },
        methods: {
            getExpirySyncDateSetting() {
                this.$store.dispatch('getAllAccountSettings', this.user.id).then(() => {
                    if (this.getAllAccountSettingsData) {
                        this.setExpirySyncDateSettingVariables()
                    }
                })
            },
            setExpirySyncDateSettingVariables() {
                // Clone expiry date setting
                this.expirySyncDateSetting = cloneDeep(this.getAllAccountSettingsData?.expirySyncDate || null);
                if (!this.expirySyncDateSetting) return;

                // Is Enabled
                this.expirySyncDateIsEnabled = this.expirySyncDateSetting.isEnabled;
                // Description
                this.expirySyncDateDescription = this.expirySyncDateSetting.description;

                // Label logic
                if (this.expirySyncDateSetting.isEnabled) {
                    var syncDate = new Date(this.expirySyncDateSetting.settingValue);
                    var expirySyncMonth = format(syncDate, 'MMMM');
                    var expirySyncDay = format(syncDate, 'd');

                    this.expirySyncDateToggleLabel = this.isFromMultiLicenseSelection
                        ? 'Preferred License Expiry Date '
                        : 'Preferred License Expiry Date is Set to: ';
                    this.expirySyncDateToggleLabelDate = `${expirySyncMonth} ${expirySyncDay}`;
                } else {
                    this.expirySyncDateToggleLabel = this.isFromMultiLicenseSelection
                        ? 'Enable Expiry Date Syncing?'
                        : 'Enable Preferred License Expiry Sync Date';
                }
            },
            confirmToggleExpirySyncDateSetting() {
                if (!this.expirySyncDateSetting.isEnabled) {
                    this.showConfirmationDialog = true;
                }
                else {
                    this.toggleExpirySyncDateSetting()
                }
            },
            cancelExpirySyncDateSettingChanges() {
                // Make sure dialogs are closed
                this.showConfirmationDialog = false;
                this.showExpirySyncDateEditor = false;

                this.setExpirySyncDateSettingVariables();
            },
            toggleExpirySyncDateSetting() {
                if (this.activeLicensesCount && this.activeLicensesCount < 2) {
                    throw new Error('User does not have multiple active licenses.');
                    return
                }
                if (!this.expirySyncDateSetting) return
                if (this.expirySyncDateSetting.isEnabled || this.expirySyncDateSetting.settingValue) {
                    //If user is turning the setting off, or turning it back on (previous value is stored), just change the enabled value and don't prompt for new value
                    this.expirySyncDateSetting.isEnabled = !this.expirySyncDateSetting.isEnabled
                    this.$store.dispatch('updateAccountSetting', this.expirySyncDateSetting).then(() => {
                        this.expirySyncDateSettingUpdated()
                    })
                }
                else {
                    this.expirySyncDateSetting.isEnabled = !this.expirySyncDateSetting.isEnabled
                    this.showExpirySyncDateEditor = true
                }
            },
            editExpirySyncDateSetting() {
                if (this.activeLicensesCount && this.activeLicensesCount < 2) {
                    throw new Error('User does not have multiple active licenses.');
                    return
                }
                this.showExpirySyncDateEditor = true
            },
            expirySyncDateSettingUpdated() {
                // Make sure dialogs are closed
                this.showConfirmationDialog = false;
                this.showExpirySyncDateEditor = false;
                // Clear cart
                this.$store.commit('reset_cart')
                // Refresh data
                this.getExpirySyncDateSetting()
                // Emit event to parent (to indicate the multi-license selection dialog needs to be refreshed)
                this.$emit('expirySyncDateSettingUpdated')
            },
        },
        computed: {
            user() { return this.$store.getters.authUser },
            ...mapState([
                'getAllAccountSettingsPending',
                'getAllAccountSettingsData',

                'getLicensesCustomerPending',
                'getLicensesCustomerData',

                'updateAccountSettingPending',
                'updateAccountSettingData',
            ]),
            screenSize() {
                return this.$q.screen
            },
            activeLicensesCount() {
                if (this.getLicensesCustomerData) {
                    var activeLicenses = this.getLicensesCustomerData?.filter(l => l.active)
                    return activeLicenses?.length
                }
                return null
            },
        },
        watch: {
            getAllAccountSettingsData(newVal, oldVal) {
                if (newVal && newVal.expirySyncDate) {
                    this.setExpirySyncDateSettingVariables()
                }
            },
        },
        created() {
            this.getExpirySyncDateSetting()
        },
    }
</script>
<style lang="scss" scoped>
    @import "../styles/quasar.variables.scss";

    .box {
        color: transparent;
        position: relative;
        border-radius: 4px;
    }

    .slide-fade-enter-active,
    .slide-fade-leave-active {
        transition: all 0.5s ease;
        overflow: hidden;
    }

    .slide-fade-enter,
    .slide-fade-leave-to {
        opacity: 0;
        height: 0;
        min-height: 0;
        margin-bottom: 0;
        padding-top: 0;
        padding-bottom: 0;
    }

    .fade-enter-active, .fade-leave-active {
        transition: opacity 0.3s ease;
    }

    .fade-enter, .fade-leave-to {
        opacity: 0;
    }

    ::v-deep .q-toggle .q-toggle__inner .q-toggle__thumb:after {
        border: 1px solid rgba(0, 0, 0, 0.2);
    }

    </style>
